import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { first, Observable, take } from 'rxjs';
import { GlobalSettings } from '../../../_core/state/global/global.model';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { GlobalQuery, GlobalService } from '../../../_core/state/global';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { AdPackageSelectDialogService } from '../../../_core/state/ad/ad-package/ad-package-select-dialog.service';
import { CreativeUnitPackageDto } from '../../../../../../api/src/creative-unit-package/models/creative-unit-package.dto';
import { ProjectsService } from '../../../_core/state/project';
import { PublicSyncingProvider } from '../../../../../../api/src/syncing-provider/syncingProvider.entity';
import { PublicDataSource } from '../../../../../../api/src/data-source/data-source.entity';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { DataSourceContext } from '../../../../../../api/src/data-source/models';
import { environment } from '../../../../environments/environment';
import { SessionQuery } from '../../../_core/state/session';
import { EventSourcePolyfill, OnMessageEvent } from 'ng-event-source';
import { CreativeUnitPackageService } from '../../../_core/state/ad/ad-unit-package.service';
import { DataSourceService } from '../../../_core/state/data-source/data-source.service';
import { AdminDataSourcesComponent } from '../../settings/admin-data-sources/admin-data-sources.component';

export interface ProjectExternalLinkDialogData {
	projectId: string;
	syncingProvider?: PublicSyncingProvider;
	dataSources: PublicDataSource[];
}
@Component({
	selector: 'app-project-syncing-provider-dialog',
	templateUrl: './project-syncing-provider-dialog.component.html',
	styleUrls: ['./project-syncing-provider-dialog.component.scss']
})
export class ProjectSyncingProviderDialogComponent implements OnInit {
	public siteCopy$: Observable<Partial<GlobalSettings>>;

	public form: UntypedFormGroup;

	public adminMode$: Observable<boolean>;
	public selectedCreativePackage: CreativeUnitPackageDto;
	public isSubmitting = false;

	constructor(
		private readonly adPackageSelectDialogService: AdPackageSelectDialogService,
		private readonly projectsService: ProjectsService,
		private readonly formBuilder: UntypedFormBuilder,
		private readonly globalService: GlobalService,
		private readonly sessionQuery: SessionQuery,
		private readonly creativeUnitPackageService: CreativeUnitPackageService,
		private readonly dataSourceService: DataSourceService,
		readonly globalQuery: GlobalQuery,
		public dialogRef: MatDialogRef<ProjectSyncingProviderDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: ProjectExternalLinkDialogData,
		private readonly dialog: MatDialog,
		private changeDetectorRef: ChangeDetectorRef
	) {
		this.adminMode$ = this.globalQuery.select('adminMode');
		this._createForm();

		// filter only the project-linking contexts in the dropdown
		data.dataSources = data.dataSources.filter(item => {
			return item.config?.context?.includes(DataSourceContext.ProjectLink);
		});

		if (data.syncingProvider) {
			this.form.patchValue({
				...data.syncingProvider,
				dataSource: data.syncingProvider?.dataSource?.id,
				config: {
					...data.syncingProvider.config,
					hideSizes: !data.syncingProvider.config.hideSizes,
					syncActionsEnabled: !!(
						data.syncingProvider.config?.syncActions?.add ||
						data.syncingProvider.config?.syncActions?.update ||
						data.syncingProvider.config?.syncActions?.delete
					)
				}
			});

			if (data.syncingProvider.config?.templatePackage) {
				this.creativeUnitPackageService
					.findOne(data.syncingProvider.config?.templatePackage)
					.pipe(take(1))
					.subscribe(selectedPackage => {
						if (selectedPackage) {
							this.selectedCreativePackage = selectedPackage;
							this.setSizeDefinitions();
						}
					});
			}
		}
	}

	ngOnInit(): void {}

	get sizes(): UntypedFormArray {
		return (this.form.controls['config'] as UntypedFormGroup)?.controls['sizes'] as UntypedFormArray;
	}

	get syncActionsEnabled(): boolean {
		return ((this.form.controls['config'] as UntypedFormGroup)?.controls['syncActionsEnabled'] as UntypedFormControl).value;
	}

	syncEnable(event: MatCheckboxChange): void {
		const syncEnabled = event.checked;

		if (!syncEnabled) {
			((this.form.controls['config'] as UntypedFormGroup)?.controls['syncActions'] as UntypedFormGroup).setValue({
				add: false,
				update: false,
				delete: false
			});
		}
	}

	async submit() {
		const formValue = this.form.value;

		//fix values before submit
		const sizes =
			formValue.config?.sizes
				?.filter(size => size.enabled)
				.map(size => ({ name: size.name, width: size.width, height: size.height })) || [];

		const reqBody = {
			dataSource: formValue.dataSource,
			config: {
				sizes,
				hideSizes: !formValue.config.hideSizes,
				syncActions: formValue.config.syncActions,
				templatePackage: formValue.config.templatePackage
			}
		};

		let requestResult;
		this.isSubmitting = true;

		if (this.data.syncingProvider?.id) {
			requestResult = await this.projectsService
				.updateSyncingProvider(this.data.projectId, this.data.syncingProvider.id, reqBody)
				.toPromise();
		} else {
			requestResult = await this.projectsService.addSyncingProvider(this.data.projectId, reqBody).toPromise();
		}

		if (requestResult?.status === 'succeeded') {
			const profile = this.sessionQuery.getProfile();
			const eventSource = new EventSourcePolyfill(`${environment.apiUrl}/projects/syncing-providers/${profile.id}`, {
				headers: { Authorization: 'Bearer ' + this.sessionQuery.getToken() }
			});

			eventSource.onmessage = async (event: OnMessageEvent) => {
				const data = JSON.parse(event.data);

				if (data.status === 'completed') {
					eventSource.close();

					const fetchResult = await this.projectsService.fetchSyncingProvider(this.data.projectId).toPromise();

					this.isSubmitting = false;
					this.dialogRef.close(fetchResult);
					this.globalService.triggerSuccessMessage(
						this.data.syncingProvider?.id
							? $localize`:Success Message@@externalCreativePackageLinkUpdatedSuccessMessageText:External creative package link updated successfully`
							: $localize`:Success Message@@externalCreativePackageLinkAddedSuccessMessageText:External creative package link added successfully`
					);
					this.changeDetectorRef.detectChanges();
				}
			};

			eventSource.onerror = (event: OnMessageEvent) => {
				eventSource.close();
				console.error('EventSource failed:', event);
				this.globalService.triggerErrorMessage(
					undefined,
					$localize`:Error Message@@e@externalCreativePackageLinkErrorMessageText:An error occurred while adding external creative package link`
				);
				this.isSubmitting = false;
			};

			return;
		} else {
			this.globalService.triggerErrorMessage(undefined, requestResult?.message);
			this.isSubmitting = false;
		}
	}

	cancel() {
		this.dialogRef.close();
	}

	async openPackageSelectorDialog() {
		const result = await this.adPackageSelectDialogService.openDialog(false, { showCancelButton: true }, false, true, true);

		if (!result?.id) {
			return;
		}

		this.selectedCreativePackage = result;
		this.form.patchValue({ config: { templatePackage: result.id, sizes: [] } });
		this.setSizeDefinitions();
	}

	setSizeDefinitions() {
		this.sizes?.clear();

		if (this.selectedCreativePackage) {
			this.selectedCreativePackage.creativeUnits?.forEach(unit => {
				const status =
					this.data?.syncingProvider?.config?.sizes?.findIndex(
						size => size.width === unit.dimensions?.width?.toString() && size.height === unit.dimensions?.height?.toString()
					) > -1;
				this.sizes?.push(
					this._createSizeDefinition(status, { name: unit.name, width: unit.dimensions.width, height: unit.dimensions.height })
				);
			});
		}
	}

	manageDataSources() {
		const dialogRef = this.dialog.open(AdminDataSourcesComponent, {
			width: '1000px',
			panelClass: ['background-color-alternative', 'max-width-lg']
		});
		// enable inline editor to avoid nested dialogs
		dialogRef.componentInstance.inlineEditor = true;

			dialogRef
				.afterClosed()
				.pipe(first())
				.subscribe(() => {
					this.dataSourceService.find({ sortBy: { id: 'slug', direction: 'ASC' } }).subscribe(response => {
						const results = response?.data?.results || [];
						this.data.dataSources = results.filter(item => item.config?.context?.includes(DataSourceContext.ProjectLink));
						this.changeDetectorRef.detectChanges();
					});
				});
	}

	private _createForm() {
		const config = this.data?.syncingProvider?.config;
		this.form = this.formBuilder.group({
			dataSource: [undefined, [Validators.required]],
			config: this.formBuilder.group({
				templatePackage: [undefined, [Validators.required]],
				hideSizes: [config?.hideSizes !== undefined ? !config?.hideSizes : false],
				sizes: this.formBuilder.array([]),
				syncActionsEnabled: [true],
				syncActions: this.formBuilder.group({
					add: [true],
					update: [true],
					delete: [true]
				})
			})
		});
	}

	private _createSizeDefinition(enabled: boolean, { name, width, height }) {
		return this.formBuilder.group({
			enabled: [enabled],
			name: [name, [Validators.required]],
			width: [`${width}`, [Validators.required]],
			height: [`${height}`, [Validators.required]]
		});
	}
}
